import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { HOME_SCULPTURE } from '../../../content/staticAssets';
import { HERO_PRODUCTION } from '../../../content/enterpriseMessaging';
import './BrandIntroduction.css';

// Document lifetime only: SPA return visits skip it, a browser refresh replays it.
let introduced = false;
function shouldShow() {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return false;
  const replay = new URLSearchParams(window.location.search).get('intro') === '1';
  if (replay) return true;
  if (window.location.hash) return false;
  return !introduced;
}

/** A brief 2D brand entrance; the existing page and header are the destination. */
export default function BrandIntroduction() {
  const [active, setActive] = useState(shouldShow);
  const overlay = useRef(null), curtain = useRef(null), host = useRef(null), markImage = useRef(null), skipButton = useRef(null), headline = useRef(null);
  const finishRef = useRef(() => {});

  useEffect(() => {
    if (!active) return undefined;
    let dead = false, transitioning = false, keyboardUsed = false;
    const timers = new Set(), animations = [];
    const page = document.querySelector('[data-home-page]');
    const logo = document.querySelector('[data-brand-logo]');
    const heroHeadline = document.querySelector('[data-brand-headline]');
    const wasInert = page?.inert;
    const priorFocus = document.activeElement;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (page) page.inert = true;
    skipButton.current?.focus({ preventScroll: true });

    // Match the destination's type and line breaks before the handoff. The real
    // H1 stays in its existing layout and remains the only page heading.
    function syncHeadline() {
      if (!heroHeadline || !headline.current) return;
      const rect = heroHeadline.getBoundingClientRect(), style = getComputedStyle(heroHeadline);
      headline.current.style.width = `${rect.width}px`;
      ['fontFamily', 'fontSize', 'fontWeight', 'fontStyle', 'lineHeight', 'letterSpacing', 'textWrap', 'wordBreak', 'color'].forEach(property => {
        headline.current.style[property] = style[property];
      });
    }
    syncHeadline();

    function later(fn, delay) { const timer = setTimeout(() => { timers.delete(timer); fn(); }, delay); timers.add(timer); return timer; }
    function finish() {
      if (dead) return;
      dead = true;
      timers.forEach(clearTimeout); animations.forEach(animation => animation.cancel());
      const restoreFocus = overlay.current?.contains(document.activeElement);
      logo?.classList.remove('brand-intro-logo-target');
      heroHeadline?.classList.remove('brand-intro-headline-target');
      if (page) page.inert = wasInert;
      if (restoreFocus) {
        const destination = priorFocus && priorFocus !== document.body && priorFocus.isConnected ? priorFocus : (keyboardUsed ? logo?.closest('a') : null);
        if (destination) destination.focus({ preventScroll: true });
        else skipButton.current?.blur();
      }
      introduced = true;
      setActive(false);
    }
    finishRef.current = finish;

    function merge() {
      if (dead || transitioning) return;
      transitioning = true;
      const mark = host.current, rect = mark.getBoundingClientRect();
      const logoRect = logo?.getBoundingClientRect();
      if (!logoRect?.width || logoRect.top < -20 || typeof mark.animate !== 'function') { finish(); return; }
      const bounds = markImage.current?.getBoundingClientRect() || rect;
      const scale = Math.min(logoRect.width / bounds.width, logoRect.height / bounds.height);
      const centerX = rect.left + rect.width / 2, centerY = rect.top + rect.height / 2;
      const dx = logoRect.left + logoRect.width / 2 - centerX - (bounds.left + bounds.width / 2 - centerX) * scale;
      const dy = logoRect.top + logoRect.height / 2 - centerY - (bounds.top + bounds.height / 2 - centerY) * scale;
      logo.classList.add('brand-intro-logo-target');
      overlay.current.dataset.phase = 'merging';
      const timing = { duration: 1050, easing: 'cubic-bezier(.65,0,.16,1)', fill: 'forwards' };
      syncHeadline();
      const text = headline.current, textRect = text?.getBoundingClientRect(), destination = heroHeadline?.getBoundingClientRect();
      if (textRect?.width && destination?.width && destination.top >= 0 && destination.top < window.innerHeight) {
        heroHeadline.classList.add('brand-intro-headline-target');
        const textX = destination.left - textRect.left, textY = destination.top - textRect.top;
        animations.push(text.animate([
          { transform: 'translate3d(0,0,0)', opacity: 1 },
          { transform: `translate3d(${textX}px,${textY}px,0)`, opacity: 1, offset: 0.8 },
          { transform: `translate3d(${textX}px,${textY}px,0)`, opacity: 0 },
        ], timing));
        animations.push(heroHeadline.animate([{ opacity: 0 }, { opacity: 0, offset: 0.8 }, { opacity: 1 }], timing));
      } else if (text) {
        animations.push(text.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 350, fill: 'forwards' }));
      }
      animations.push(mark.animate([{ transform: 'translate3d(0,0,0) scale(1)' }, { transform: `translate3d(${dx}px,${dy}px,0) scale(${scale})` }], timing));
      animations.push(curtain.current.animate([{ transform: 'scaleY(1)' }, { transform: 'scaleY(0)' }], { ...timing, duration: 950 }));
      animations.push(skipButton.current.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 200, fill: 'forwards' }));
      later(() => {
        logo.classList.remove('brand-intro-logo-target');
        animations.push(mark.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 140, fill: 'forwards' }));
        later(finish, 150);
      }, 1050);
    }

    overlay.current.dataset.phase = 'holding';
    later(merge, 1600);
    later(finish, 5200);

    function keydown(event) {
      keyboardUsed = true;
      if (event.key === 'Escape') finish();
      if (event.key === 'Tab') { event.preventDefault(); skipButton.current?.focus({ preventScroll: true }); }
    }
    const motionChanged = () => { if (reduced.matches) finish(); };
    window.addEventListener('keydown', keydown);
    window.addEventListener('wheel', finish, { passive: true });
    window.addEventListener('touchmove', finish, { passive: true });
    const resized = () => { if (transitioning) finish(); else syncHeadline(); };
    window.addEventListener('resize', resized);
    reduced.addEventListener('change', motionChanged);
    return () => {
      dead = true; timers.forEach(clearTimeout); animations.forEach(animation => animation.cancel());
      logo?.classList.remove('brand-intro-logo-target');
      heroHeadline?.classList.remove('brand-intro-headline-target');
      if (page) page.inert = wasInert;
      window.removeEventListener('keydown', keydown); window.removeEventListener('wheel', finish); window.removeEventListener('touchmove', finish); window.removeEventListener('resize', resized);
      reduced.removeEventListener('change', motionChanged);
    };
  }, [active]);

  if (!active) return null;
  return createPortal(
    <div ref={overlay} className="brand-introduction" role="dialog" aria-modal="true" aria-label="MyOrgLab introduction" aria-describedby="brand-introduction-message" data-phase="opening">
      <div ref={curtain} className="brand-introduction-curtain" />
      <div className="brand-introduction-stage">
        <div ref={host} className="brand-introduction-mark" aria-hidden="true">
          <img
            ref={markImage}
            src={HOME_SCULPTURE.logoSource}
            alt=""
            draggable="false"
            decoding="async"
            onError={(event) => {
              const node = event.currentTarget;
              if (node.dataset.fallback === 'poster') return;
              if (node.dataset.fallback === 'wordmark') {
                node.dataset.fallback = 'poster';
                node.src = HOME_SCULPTURE.poster;
                return;
              }
              node.dataset.fallback = 'wordmark';
              node.src = HOME_SCULPTURE.wordmark || HOME_SCULPTURE.mark;
            }}
          />
        </div>
        <p ref={headline} id="brand-introduction-message" className="brand-introduction-headline text-balance break-words font-display text-[1.85rem] font-semibold leading-[1.12] tracking-tight text-ink-900 sm:text-5xl lg:text-[3.5rem]">
          {HERO_PRODUCTION.titleLead}<span className="text-brand-600">{HERO_PRODUCTION.titleHighlight}</span>
        </p>
      </div>
      <button ref={skipButton} className="brand-introduction-skip" type="button" onClick={() => finishRef.current()}>Skip introduction <span aria-hidden="true">↗</span></button>
    </div>, document.body,
  );
}
